import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from '../store';
import {
  Box,
  Paper,
  Typography,
  Button,
  Grid,
  Card,
  CardContent,
  FormControl,
  FormControlLabel,
  Switch,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Chip,
  IconButton,
  Tabs,
  Tab,
  Select,
  MenuItem,
  InputLabel,
  LinearProgress,
  CircularProgress,
} from '@mui/material';
import { toast } from 'react-toastify';
import {
  usePerformFullSyncMutation,
  usePushDataToCentralMutation,
  usePullDataFromCentralMutation,
  useInitializeStoreFromCentralMutation,
  useGetSyncStatusQuery,
  useGetEnhancedSyncStatusQuery,
} from '../services/sync.service';
import {
  Settings as SettingsIcon,
  Inventory as InventoryIcon,
  MonetizationOn as PriceIcon,
  Receipt as TaxIcon,
  Notifications as NotificationIcon,
  Security as SecurityIcon,
  Save as SaveIcon,
  Restore as RestoreIcon,
  Warning as WarningIcon,
  CheckCircle as SuccessIcon,
  Info as InfoIcon,
  AccountBalance as CompanyIcon,
  Language as LanguageIcon,
  Palette as ThemeIcon,
  Sync as SyncIcon,
  CloudUpload as CloudUploadIcon,
  CloudDownload as CloudDownloadIcon,
  Refresh as RefreshIcon,
  StoreMallDirectory as InitializeIcon,
} from '@mui/icons-material';

interface SystemSettings {
  // Configuraciones de inventario
  inventory: {
    allowNegativeStock: boolean;
    negativeStockWarningLimit: number;
    autoReorderEnabled: boolean;
    autoReorderLevel: number;
    lowStockWarningEnabled: boolean;
    lowStockThreshold: number;
    trackInventoryMovements: boolean;
  };
  
  // Configuraciones de ventas
  sales: {
    requireCustomerName: boolean;
    allowPartialPayments: boolean;
    defaultPaymentMethod: string;
    printReceiptAutomatically: boolean;
    validateStockOnSale: boolean;
    allowSaleWithoutStock: boolean;
  };
  
  // Configuraciones fiscales
  fiscal: {
    defaultTaxRate: number;
    taxIncludedInPrice: boolean;
    fiscalYear: number;
    invoicePrefix: string;
    receiptFooter: string;
    companyName: string;
    companyAddress: string;
    companyPhone: string;
    companyEmail: string;
    rtn: string; // Registro Tributario Nacional Honduras
  };
  
  // Configuraciones de notificaciones
  notifications: {
    lowStockAlerts: boolean;
    negativeStockAlerts: boolean;
    dailySalesReport: boolean;
    emailNotifications: boolean;
    soundAlerts: boolean;
  };
  
  // Configuraciones del sistema
  system: {
    language: string;
    currency: string;
    dateFormat: string;
    timeFormat: string;
    theme: string;
    backupFrequency: string;
    sessionTimeout: number;
  };
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`settings-tabpanel-${index}`}
      aria-labelledby={`settings-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
    </div>
  );
}

const SettingsPage: React.FC = () => {
  const [currentTab, setCurrentTab] = useState(0);
  
  // Sync-related state
  const authState = useSelector((state: RootState) => state.auth);
  const [performFullSync, { isLoading: isFullSyncing }] = usePerformFullSyncMutation();
  const [pushData, { isLoading: isPushing }] = usePushDataToCentralMutation();
  const [pullData, { isLoading: isPulling }] = usePullDataFromCentralMutation();
  const [initializeStore, { isLoading: isInitializing }] = useInitializeStoreFromCentralMutation();
  
  const { data: syncStatus, refetch: refetchStatus } = useGetSyncStatusQuery(undefined, {
    pollingInterval: 30000, // Poll every 30 seconds
  });
  
  const { data: enhancedSyncStatus, refetch: refetchEnhancedStatus } = useGetEnhancedSyncStatusQuery(undefined, {
    pollingInterval: 30000, // Poll every 30 seconds
  });
  
  const [syncProgress, setSyncProgress] = useState(0);
  const [syncStep, setSyncStep] = useState('');
  const [syncRecords, setSyncRecords] = useState({ sent: 0, received: 0, total: 0 });
  
  const [settings, setSettings] = useState<SystemSettings>({
    inventory: {
      allowNegativeStock: true,
      negativeStockWarningLimit: -10,
      autoReorderEnabled: false,
      autoReorderLevel: 10,
      lowStockWarningEnabled: true,
      lowStockThreshold: 5,
      trackInventoryMovements: true,
    },
    sales: {
      requireCustomerName: false,
      allowPartialPayments: false,
      defaultPaymentMethod: 'CASH',
      printReceiptAutomatically: true,
      validateStockOnSale: false, // Desactivado para permitir ventas con stock negativo
      allowSaleWithoutStock: true,
    },
    fiscal: {
      defaultTaxRate: 15,
      taxIncludedInPrice: false,
      fiscalYear: new Date().getFullYear(),
      invoicePrefix: 'PJ',
      receiptFooter: 'Gracias por su compra - Plásticos Juticalpa',
      companyName: 'Plásticos Juticalpa',
      companyAddress: 'Juticalpa, Olancho, Honduras',
      companyPhone: '+504 2785-0000',
      companyEmail: 'info@plasticosjuticalpa.hn',
      rtn: '08011234567890',
    },
    notifications: {
      lowStockAlerts: true,
      negativeStockAlerts: true,
      dailySalesReport: true,
      emailNotifications: false,
      soundAlerts: true,
    },
    system: {
      language: 'es',
      currency: 'HNL',
      dateFormat: 'DD/MM/YYYY',
      timeFormat: '24h',
      theme: 'light',
      backupFrequency: 'daily',
      sessionTimeout: 480, // 8 horas en minutos
    },
  });
  
  const [hasChanges, setHasChanges] = useState(false);
  const [saveDialog, setSaveDialog] = useState(false);
  const [resetDialog, setResetDialog] = useState(false);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setCurrentTab(newValue);
  };

  const updateSettings = (category: keyof SystemSettings, key: string, value: any) => {
    setSettings(prev => ({
      ...prev,
      [category]: {
        ...prev[category],
        [key]: value,
      },
    }));
    setHasChanges(true);
  };

  const saveSettings = () => {
    console.log('Guardando configuraciones:', settings);
    // Aquí se enviarían las configuraciones a la API
    setHasChanges(false);
    setSaveDialog(false);
    alert('✅ Configuraciones guardadas exitosamente');
  };

  const resetToDefaults = () => {
    // Restablecer configuraciones por defecto
    setSettings({
      inventory: {
        allowNegativeStock: false,
        negativeStockWarningLimit: 0,
        autoReorderEnabled: false,
        autoReorderLevel: 10,
        lowStockWarningEnabled: true,
        lowStockThreshold: 5,
        trackInventoryMovements: true,
      },
      sales: {
        requireCustomerName: false,
        allowPartialPayments: false,
        defaultPaymentMethod: 'CASH',
        printReceiptAutomatically: true,
        validateStockOnSale: true,
        allowSaleWithoutStock: false,
      },
      fiscal: {
        defaultTaxRate: 15,
        taxIncludedInPrice: false,
        fiscalYear: new Date().getFullYear(),
        invoicePrefix: 'INV',
        receiptFooter: 'Gracias por su compra',
        companyName: 'Mi Empresa',
        companyAddress: '',
        companyPhone: '',
        companyEmail: '',
        rtn: '',
      },
      notifications: {
        lowStockAlerts: true,
        negativeStockAlerts: false,
        dailySalesReport: false,
        emailNotifications: false,
        soundAlerts: true,
      },
      system: {
        language: 'es',
        currency: 'HNL',
        dateFormat: 'DD/MM/YYYY',
        timeFormat: '24h',
        theme: 'light',
        backupFrequency: 'weekly',
        sessionTimeout: 480,
      },
    });
    setHasChanges(true);
    setResetDialog(false);
  };

  // Sync handlers with progress tracking
  const updateSyncProgress = (step: string, progress: number, records?: { sent?: number; received?: number; total?: number }) => {
    setSyncStep(step);
    setSyncProgress(progress);
    if (records) {
      setSyncRecords(prev => ({ ...prev, ...records }));
    }
  };

  const handlePushData = async () => {
    try {
      updateSyncProgress('Iniciando envío de datos...', 10);
      
      setTimeout(() => updateSyncProgress('Conectando con servidor central...', 20), 1000);
      setTimeout(() => updateSyncProgress('Recopilando datos locales...', 40), 3000);
      setTimeout(() => updateSyncProgress('Enviando datos al servidor...', 70), 5000);
      
      const result = await pushData().unwrap();
      updateSyncProgress('Datos enviados exitosamente', 100, { sent: result.recordsSent || 0 });
      
      toast.success(`✅ ${result.recordsSent || 0} registros enviados al servidor central`);
      setTimeout(() => {
        setSyncProgress(0);
        setSyncStep('');
      }, 3000);
    } catch (error: any) {
      setSyncProgress(0);
      setSyncStep('');
      const errorMsg = error.data?.message || error.message || 'Error de conexión';
      toast.error(`❌ Error enviando datos: ${errorMsg}`);
    }
  };

  const handlePullData = async () => {
    try {
      updateSyncProgress('Iniciando recepción de datos...', 10);
      
      setTimeout(() => updateSyncProgress('Conectando con servidor central...', 30), 1000);
      setTimeout(() => updateSyncProgress('Descargando datos del servidor...', 60), 3000);
      setTimeout(() => updateSyncProgress('Procesando datos recibidos...', 80), 8000);
      
      const result = await pullData().unwrap();
      updateSyncProgress('Datos recibidos exitosamente', 100, { received: result.recordsReceived || 0 });
      
      toast.success(`📥 ${result.recordsReceived || 0} registros recibidos del servidor central`);
      setTimeout(() => {
        setSyncProgress(0);
        setSyncStep('');
      }, 3000);
    } catch (error: any) {
      setSyncProgress(0);
      setSyncStep('');
      const errorMsg = error.data?.message || error.message || 'Error de conexión';
      toast.error(`❌ Error recibiendo datos: ${errorMsg}`);
    }
  };

  const handleFullSync = async () => {
    try {
      setSyncRecords({ sent: 0, received: 0, total: 0 });
      updateSyncProgress('Iniciando sincronización completa...', 5);
      
      setTimeout(() => updateSyncProgress('Enviando datos locales al servidor...', 20), 2000);
      setTimeout(() => updateSyncProgress('Procesando datos en servidor...', 50), 15000);
      setTimeout(() => updateSyncProgress('Recibiendo datos del servidor...', 75), 25000);
      setTimeout(() => updateSyncProgress('Finalizando sincronización...', 90), 35000);
      
      const result = await performFullSync().unwrap();
      const sent = result.recordsSent || 0;
      const received = result.recordsReceived || 0;
      
      updateSyncProgress('Sincronización completa exitosa', 100, { sent, received, total: sent + received });
      toast.success(`✅ Sincronización completa: ${sent} enviados, ${received} recibidos`);
      
      setTimeout(() => {
        setSyncProgress(0);
        setSyncStep('');
      }, 5000);
    } catch (error: any) {
      setSyncProgress(0);
      setSyncStep('');
      const errorMsg = error.data?.message || error.message || 'Error de conexión';
      toast.error(`❌ Error en sincronización: ${errorMsg}`);
    }
  };

  const handleInitializeStore = async () => {
    if (!window.confirm(
      '⚠️ ADVERTENCIA: Esta acción descargará TODOS los datos del servidor central y reemplazará los datos locales.\n\n' +
      'Esto incluye productos, inventario, usuarios, ventas, comisiones, etc.\n\n' +
      '¿Está seguro de que desea continuar?'
    )) {
      return;
    }

    try {
      updateSyncProgress('Preparando inicialización de tienda...', 5);
      setTimeout(() => updateSyncProgress('Conectando con servidor central...', 15), 1000);
      setTimeout(() => updateSyncProgress('Descargando datos completos...', 40), 5000);
      setTimeout(() => updateSyncProgress('Procesando e instalando datos...', 70), 20000);
      setTimeout(() => updateSyncProgress('Configurando tienda local...', 90), 40000);
      
      const result = await initializeStore().unwrap();
      updateSyncProgress('Tienda inicializada exitosamente', 100, { received: result.recordsReceived });
      
      toast.success(`✅ Tienda inicializada: ${result.recordsReceived} registros instalados`);
      setTimeout(() => {
        window.location.reload();
      }, 3000);
    } catch (error: any) {
      setSyncProgress(0);
      setSyncStep('');
      const errorMsg = error.data?.message || error.message || 'Error de conexión';
      toast.error(`❌ Error inicializando tienda: ${errorMsg}`);
    }
  };

  const isAnyLoading = isFullSyncing || isPushing || isPulling || isInitializing;

  return (
    <Box>
      {/* Encabezado */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h4" component="h1" gutterBottom fontWeight="bold">
            Configuraciones del Sistema
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Personaliza el comportamiento del sistema de inventario
          </Typography>
        </Box>
        
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            variant="outlined"
            startIcon={<RestoreIcon />}
            onClick={() => setResetDialog(true)}
          >
            Restablecer
          </Button>
          <Button
            variant="contained"
            startIcon={<SaveIcon />}
            onClick={() => setSaveDialog(true)}
            disabled={!hasChanges}
          >
            Guardar Cambios
          </Button>
        </Box>
      </Box>

      {hasChanges && (
        <Alert severity="warning" sx={{ mb: 3 }}>
          <Typography variant="subtitle2">
            Tienes cambios sin guardar
          </Typography>
          <Typography variant="body2">
            Recuerda guardar los cambios antes de salir de esta página.
          </Typography>
        </Alert>
      )}

      {/* Pestañas de configuración */}
      <Paper>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs value={currentTab} onChange={handleTabChange} aria-label="settings tabs">
            <Tab 
              label="Inventario" 
              icon={<InventoryIcon />} 
              iconPosition="start"
            />
            <Tab 
              label="Ventas" 
              icon={<PriceIcon />} 
              iconPosition="start"
            />
            <Tab 
              label="Fiscal" 
              icon={<TaxIcon />} 
              iconPosition="start"
            />
            <Tab 
              label="Notificaciones" 
              icon={<NotificationIcon />} 
              iconPosition="start"
            />
            <Tab 
              label="Sincronización" 
              icon={<SyncIcon />} 
              iconPosition="start"
            />
            <Tab 
              label="Sistema" 
              icon={<SettingsIcon />} 
              iconPosition="start"
            />
          </Tabs>
        </Box>

        {/* Panel de Inventario */}
        <TabPanel value={currentTab} index={0}>
          <Typography variant="h6" gutterBottom fontWeight="bold">
            Configuraciones de Inventario
          </Typography>
          
          <Grid container spacing={3}>
            <Grid size={{ xs: 12, md: 6 }}>
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                    Stock Negativo
                  </Typography>
                  
                  <FormControlLabel
                    control={
                      <Switch
                        checked={settings.inventory.allowNegativeStock}
                        onChange={(e) => updateSettings('inventory', 'allowNegativeStock', e.target.checked)}
                      />
                    }
                    label="Permitir inventario negativo"
                  />
                  
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Permite vender productos aunque no haya stock disponible
                  </Typography>
                  
                  {settings.inventory.allowNegativeStock && (
                    <TextField
                      fullWidth
                      label="Límite de advertencia (stock negativo)"
                      type="number"
                      value={settings.inventory.negativeStockWarningLimit}
                      onChange={(e) => updateSettings('inventory', 'negativeStockWarningLimit', parseInt(e.target.value) || 0)}
                      size="small"
                      helperText="Mostrar advertencia cuando el stock sea menor a este valor"
                      sx={{ mt: 1 }}
                    />
                  )}
                  
                  {settings.inventory.allowNegativeStock && (
                    <Alert severity="info" sx={{ mt: 2 }}>
                      <Typography variant="body2">
                        Con esta opción activada, el sistema permitirá procesar ventas incluso cuando no haya suficiente stock. 
                        Los productos aparecerán con stock negativo.
                      </Typography>
                    </Alert>
                  )}
                </CardContent>
              </Card>
            </Grid>
            
            <Grid size={{ xs: 12, md: 6 }}>
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                    Alertas de Stock
                  </Typography>
                  
                  <FormControlLabel
                    control={
                      <Switch
                        checked={settings.inventory.lowStockWarningEnabled}
                        onChange={(e) => updateSettings('inventory', 'lowStockWarningEnabled', e.target.checked)}
                      />
                    }
                    label="Alertas de stock bajo"
                  />
                  
                  {settings.inventory.lowStockWarningEnabled && (
                    <TextField
                      fullWidth
                      label="Umbral de stock bajo"
                      type="number"
                      value={settings.inventory.lowStockThreshold}
                      onChange={(e) => updateSettings('inventory', 'lowStockThreshold', parseInt(e.target.value) || 0)}
                      size="small"
                      sx={{ mt: 2 }}
                      inputProps={{ min: 0 }}
                    />
                  )}
                  
                  <FormControlLabel
                    control={
                      <Switch
                        checked={settings.inventory.trackInventoryMovements}
                        onChange={(e) => updateSettings('inventory', 'trackInventoryMovements', e.target.checked)}
                      />
                    }
                    label="Rastrear movimientos de inventario"
                    sx={{ mt: 2 }}
                  />
                </CardContent>
              </Card>
            </Grid>
            
            <Grid size={{ xs: 12, md: 6 }}>
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                    Reorden Automático
                  </Typography>
                  
                  <FormControlLabel
                    control={
                      <Switch
                        checked={settings.inventory.autoReorderEnabled}
                        onChange={(e) => updateSettings('inventory', 'autoReorderEnabled', e.target.checked)}
                      />
                    }
                    label="Activar reorden automático"
                  />
                  
                  {settings.inventory.autoReorderEnabled && (
                    <TextField
                      fullWidth
                      label="Nivel de reorden"
                      type="number"
                      value={settings.inventory.autoReorderLevel}
                      onChange={(e) => updateSettings('inventory', 'autoReorderLevel', parseInt(e.target.value) || 0)}
                      size="small"
                      sx={{ mt: 2 }}
                      helperText="Generar orden de compra cuando el stock baje a este nivel"
                    />
                  )}
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </TabPanel>

        {/* Panel de Ventas */}
        <TabPanel value={currentTab} index={1}>
          <Typography variant="h6" gutterBottom fontWeight="bold">
            Configuraciones de Ventas
          </Typography>
          
          <Grid container spacing={3}>
            <Grid size={{ xs: 12, md: 6 }}>
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                    Validación de Ventas
                  </Typography>
                  
                  <FormControlLabel
                    control={
                      <Switch
                        checked={settings.sales.validateStockOnSale}
                        onChange={(e) => updateSettings('sales', 'validateStockOnSale', e.target.checked)}
                      />
                    }
                    label="Validar stock al vender"
                  />
                  
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    {settings.sales.validateStockOnSale 
                      ? 'Bloquear ventas si no hay stock suficiente'
                      : 'Permitir ventas sin validar stock disponible'
                    }
                  </Typography>
                  
                  <FormControlLabel
                    control={
                      <Switch
                        checked={settings.sales.allowSaleWithoutStock}
                        onChange={(e) => updateSettings('sales', 'allowSaleWithoutStock', e.target.checked)}
                      />
                    }
                    label="Permitir venta sin stock"
                  />
                  
                  <FormControlLabel
                    control={
                      <Switch
                        checked={settings.sales.requireCustomerName}
                        onChange={(e) => updateSettings('sales', 'requireCustomerName', e.target.checked)}
                      />
                    }
                    label="Requerir nombre del cliente"
                    sx={{ mt: 1 }}
                  />
                </CardContent>
              </Card>
            </Grid>
            
            <Grid size={{ xs: 12, md: 6 }}>
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                    Métodos de Pago
                  </Typography>
                  
                  <FormControl fullWidth sx={{ mb: 2 }}>
                    <InputLabel>Método de pago predeterminado</InputLabel>
                    <Select
                      value={settings.sales.defaultPaymentMethod}
                      onChange={(e) => updateSettings('sales', 'defaultPaymentMethod', e.target.value)}
                      label="Método de pago predeterminado"
                    >
                      <MenuItem value="CASH">💵 Efectivo</MenuItem>
                      <MenuItem value="CARD">💳 Tarjeta</MenuItem>
                      <MenuItem value="TRANSFER">🏦 Transferencia</MenuItem>
                    </Select>
                  </FormControl>
                  
                  <FormControlLabel
                    control={
                      <Switch
                        checked={settings.sales.allowPartialPayments}
                        onChange={(e) => updateSettings('sales', 'allowPartialPayments', e.target.checked)}
                      />
                    }
                    label="Permitir pagos parciales"
                  />
                  
                  <FormControlLabel
                    control={
                      <Switch
                        checked={settings.sales.printReceiptAutomatically}
                        onChange={(e) => updateSettings('sales', 'printReceiptAutomatically', e.target.checked)}
                      />
                    }
                    label="Imprimir recibo automáticamente"
                    sx={{ mt: 1 }}
                  />
                </CardContent>
              </Card>
            </Grid>
          </Grid>
          
          {/* Resumen de configuración actual */}
          <Box sx={{ mt: 3 }}>
            <Alert 
              severity={settings.sales.allowSaleWithoutStock ? "warning" : "info"}
            >
              <Typography variant="subtitle2">
                Configuración Actual de Inventario en Ventas
              </Typography>
              <Typography variant="body2">
                {settings.sales.allowSaleWithoutStock 
                  ? "🟡 Las ventas se procesarán incluso sin stock suficiente. Los productos pueden quedar con inventario negativo."
                  : "🔒 Las ventas requieren stock disponible para procesar."
                }
              </Typography>
            </Alert>
          </Box>
        </TabPanel>

        {/* Panel Fiscal */}
        <TabPanel value={currentTab} index={2}>
          <Typography variant="h6" gutterBottom fontWeight="bold">
            Configuraciones Fiscales
          </Typography>
          
          <Grid container spacing={3}>
            <Grid size={{ xs: 12, md: 6 }}>
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                    Impuestos
                  </Typography>
                  
                  <TextField
                    fullWidth
                    label="Tasa de ISV predeterminada (%)"
                    type="number"
                    value={settings.fiscal.defaultTaxRate}
                    onChange={(e) => updateSettings('fiscal', 'defaultTaxRate', parseFloat(e.target.value) || 0)}
                    sx={{ mb: 2 }}
                    inputProps={{ min: 0, max: 100, step: 0.1 }}
                    helperText="Impuesto Sobre Ventas en Honduras (15%)"
                  />
                  
                  <FormControlLabel
                    control={
                      <Switch
                        checked={settings.fiscal.taxIncludedInPrice}
                        onChange={(e) => updateSettings('fiscal', 'taxIncludedInPrice', e.target.checked)}
                      />
                    }
                    label="ISV incluido en precios"
                  />
                  
                  <TextField
                    fullWidth
                    label="Año fiscal"
                    type="number"
                    value={settings.fiscal.fiscalYear}
                    onChange={(e) => updateSettings('fiscal', 'fiscalYear', parseInt(e.target.value) || new Date().getFullYear())}
                    sx={{ mt: 2 }}
                  />
                </CardContent>
              </Card>
            </Grid>
            
            <Grid size={{ xs: 12, md: 6 }}>
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                    Información de la Empresa
                  </Typography>
                  
                  <TextField
                    fullWidth
                    label="Nombre de la empresa"
                    value={settings.fiscal.companyName}
                    onChange={(e) => updateSettings('fiscal', 'companyName', e.target.value)}
                    sx={{ mb: 2 }}
                  />
                  
                  <TextField
                    fullWidth
                    label="RTN (Registro Tributario Nacional)"
                    value={settings.fiscal.rtn}
                    onChange={(e) => updateSettings('fiscal', 'rtn', e.target.value)}
                    sx={{ mb: 2 }}
                    helperText="Número de registro tributario en Honduras"
                  />
                  
                  <TextField
                    fullWidth
                    label="Dirección"
                    multiline
                    rows={2}
                    value={settings.fiscal.companyAddress}
                    onChange={(e) => updateSettings('fiscal', 'companyAddress', e.target.value)}
                    sx={{ mb: 2 }}
                  />
                  
                  <TextField
                    fullWidth
                    label="Teléfono"
                    value={settings.fiscal.companyPhone}
                    onChange={(e) => updateSettings('fiscal', 'companyPhone', e.target.value)}
                    sx={{ mb: 2 }}
                  />
                  
                  <TextField
                    fullWidth
                    label="Email"
                    type="email"
                    value={settings.fiscal.companyEmail}
                    onChange={(e) => updateSettings('fiscal', 'companyEmail', e.target.value)}
                  />
                </CardContent>
              </Card>
            </Grid>
            
            <Grid size={{ xs: 12 }}>
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                    Facturación
                  </Typography>
                  
                  <Grid container spacing={2}>
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <TextField
                        fullWidth
                        label="Prefijo de facturas"
                        value={settings.fiscal.invoicePrefix}
                        onChange={(e) => updateSettings('fiscal', 'invoicePrefix', e.target.value)}
                        helperText="Ej: INV, PJ, FACT"
                      />
                    </Grid>
                    
                    <Grid size={{ xs: 12 }}>
                      <TextField
                        fullWidth
                        label="Pie de página de recibos"
                        multiline
                        rows={3}
                        value={settings.fiscal.receiptFooter}
                        onChange={(e) => updateSettings('fiscal', 'receiptFooter', e.target.value)}
                        placeholder="Mensaje que aparecerá al final de cada recibo..."
                      />
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </TabPanel>

        {/* Panel de Notificaciones */}
        <TabPanel value={currentTab} index={3}>
          <Typography variant="h6" gutterBottom fontWeight="bold">
            Configuraciones de Notificaciones
          </Typography>
          
          <Grid container spacing={3}>
            <Grid size={{ xs: 12, md: 6 }}>
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                    Alertas de Inventario
                  </Typography>
                  
                  <List>
                    <ListItem>
                      <ListItemIcon>
                        <WarningIcon color="warning" />
                      </ListItemIcon>
                      <ListItemText primary="Stock bajo" />
                      <Switch
                        checked={settings.notifications.lowStockAlerts}
                        onChange={(e) => updateSettings('notifications', 'lowStockAlerts', e.target.checked)}
                      />
                    </ListItem>
                    
                    <ListItem>
                      <ListItemIcon>
                        <WarningIcon color="error" />
                      </ListItemIcon>
                      <ListItemText primary="Stock negativo" />
                      <Switch
                        checked={settings.notifications.negativeStockAlerts}
                        onChange={(e) => updateSettings('notifications', 'negativeStockAlerts', e.target.checked)}
                      />
                    </ListItem>
                  </List>
                </CardContent>
              </Card>
            </Grid>
            
            <Grid size={{ xs: 12, md: 6 }}>
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                    Reportes y Comunicaciones
                  </Typography>
                  
                  <List>
                    <ListItem>
                      <ListItemIcon>
                        <InfoIcon color="info" />
                      </ListItemIcon>
                      <ListItemText primary="Reporte diario de ventas" />
                      <Switch
                        checked={settings.notifications.dailySalesReport}
                        onChange={(e) => updateSettings('notifications', 'dailySalesReport', e.target.checked)}
                      />
                    </ListItem>
                    
                    <ListItem>
                      <ListItemIcon>
                        <NotificationIcon />
                      </ListItemIcon>
                      <ListItemText primary="Notificaciones por email" />
                      <Switch
                        checked={settings.notifications.emailNotifications}
                        onChange={(e) => updateSettings('notifications', 'emailNotifications', e.target.checked)}
                      />
                    </ListItem>
                    
                    <ListItem>
                      <ListItemIcon>
                        <NotificationIcon />
                      </ListItemIcon>
                      <ListItemText primary="Alertas sonoras" />
                      <Switch
                        checked={settings.notifications.soundAlerts}
                        onChange={(e) => updateSettings('notifications', 'soundAlerts', e.target.checked)}
                      />
                    </ListItem>
                  </List>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </TabPanel>

        {/* Panel de Sincronización */}
        <TabPanel value={currentTab} index={4}>
          <Typography variant="h6" gutterBottom fontWeight="bold">
            Sincronización de Datos
          </Typography>
          
          <Grid container spacing={3}>
            {/* Status Card */}
            <Grid size={{ xs: 12 }}>
              <Card variant="outlined">
                <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                    <Typography variant="subtitle1" fontWeight="bold">
                      Estado de Sincronización
                    </Typography>
                    <Chip 
                      label={(enhancedSyncStatus?.syncEnabled || syncStatus?.syncEnabled) ? 'Activo' : 'Desactivado'}
                      color={(enhancedSyncStatus?.syncEnabled || syncStatus?.syncEnabled) ? 'success' : 'default'}
                      size="small"
                    />
                  </Box>
                  
                  {syncStatus && (
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="body2" color="text.secondary">
                        Última sincronización: {syncStatus.lastSync ? 
                          new Date(syncStatus.lastSync).toLocaleString() : 
                          'Nunca'
                        }
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Cambios pendientes: {syncStatus.pendingChanges || 0}
                      </Typography>
                    </Box>
                  )}
                  
                  {/* Progress Section */}
                  {(isAnyLoading || syncProgress > 0) && (
                    <Box sx={{ mb: 3 }}>
                      <Typography variant="body2" sx={{ mb: 1 }}>
                        {syncStep}
                      </Typography>
                      <LinearProgress 
                        variant="determinate" 
                        value={syncProgress} 
                        sx={{ height: 8, borderRadius: 4 }}
                      />
                      <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                        {syncProgress.toFixed(0)}%
                      </Typography>
                    </Box>
                  )}
                  
                  {/* Record Counters */}
                  {(syncRecords.sent > 0 || syncRecords.received > 0 || syncRecords.total > 0) && (
                    <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                      {syncRecords.sent > 0 && (
                        <Chip 
                          label={`📤 ${syncRecords.sent} enviados`}
                          color="primary"
                          size="small"
                        />
                      )}
                      {syncRecords.received > 0 && (
                        <Chip 
                          label={`📥 ${syncRecords.received} recibidos`}
                          color="secondary"
                          size="small"
                        />
                      )}
                      {syncRecords.total > 0 && (
                        <Chip 
                          label={`📊 ${syncRecords.total} total`}
                          color="success"
                          size="small"
                        />
                      )}
                    </Box>
                  )}
                </CardContent>
              </Card>
            </Grid>
            
            {/* Manual Sync Operations */}
            <Grid size={{ xs: 12, md: 6 }}>
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                    Operaciones de Sincronización
                  </Typography>
                  
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <Button
                      variant="contained"
                      startIcon={isPushing ? <CircularProgress size={20} /> : <CloudUploadIcon />}
                      onClick={handlePushData}
                      disabled={isAnyLoading}
                      fullWidth
                    >
                      Enviar Datos
                    </Button>
                    
                    <Button
                      variant="outlined"
                      startIcon={isPulling ? <CircularProgress size={20} /> : <CloudDownloadIcon />}
                      onClick={handlePullData}
                      disabled={isAnyLoading}
                      fullWidth
                    >
                      Recibir Datos
                    </Button>
                    
                    <Button
                      variant="contained"
                      color="secondary"
                      startIcon={isFullSyncing ? <CircularProgress size={20} /> : <RefreshIcon />}
                      onClick={handleFullSync}
                      disabled={isAnyLoading}
                      fullWidth
                    >
                      Sincronización Completa
                    </Button>
                  </Box>
                  
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                    • <strong>Enviar:</strong> Solo envía cambios locales al servidor<br/>
                    • <strong>Recibir:</strong> Solo descarga cambios del servidor<br/>
                    • <strong>Completa:</strong> Envía y recibe todos los cambios
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            
            {/* Advanced Operations */}
            <Grid size={{ xs: 12, md: 6 }}>
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="subtitle1" fontWeight="bold" gutterBottom color="warning.main">
                    Operaciones Avanzadas
                  </Typography>
                  
                  <Button
                    variant="outlined"
                    color="warning"
                    startIcon={isInitializing ? <CircularProgress size={20} /> : <InitializeIcon />}
                    onClick={handleInitializeStore}
                    disabled={isAnyLoading}
                    fullWidth
                    sx={{ mb: 2 }}
                  >
                    Inicializar Tienda
                  </Button>
                  
                  <Alert severity="warning" sx={{ fontSize: '0.8rem' }}>
                    <Typography variant="body2">
                      <strong>⚠️ Cuidado:</strong> La inicialización descarga TODOS los datos del servidor central 
                      y reemplaza completamente los datos locales. Usar solo para configuración inicial de tienda.
                    </Typography>
                  </Alert>
                </CardContent>
              </Card>
            </Grid>
            
            {/* Sync Information */}
            <Grid size={{ xs: 12 }}>
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                    Información de Sincronización
                  </Typography>
                  
                  <Grid container spacing={2}>
                    <Grid size={{ xs: 12, sm: 4 }}>
                      <Box sx={{ textAlign: 'center', p: 2 }}>
                        <Typography variant="h4" color="primary.main">
                          {syncStatus?.pendingChanges || 0}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Cambios Pendientes
                        </Typography>
                      </Box>
                    </Grid>
                    
                    <Grid size={{ xs: 12, sm: 4 }}>
                      <Box sx={{ textAlign: 'center', p: 2 }}>
                        <Typography variant="h4" color="success.main">
                          {syncRecords.sent}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Registros Enviados
                        </Typography>
                      </Box>
                    </Grid>
                    
                    <Grid size={{ xs: 12, sm: 4 }}>
                      <Box sx={{ textAlign: 'center', p: 2 }}>
                        <Typography variant="h4" color="info.main">
                          {syncRecords.received}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Registros Recibidos
                        </Typography>
                      </Box>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Grid>

            {/* Enhanced Statistics Breakdown */}
            {enhancedSyncStatus?.statistics && (
              <Grid size={{ xs: 12 }}>
                <Card variant="outlined">
                  <CardContent>
                    <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                      Desglose de Registros por Tipo
                    </Typography>
                    
                    <Grid container spacing={2}>
                      <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                        <Box sx={{ textAlign: 'center', p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                          <Typography variant="h5" color="primary.main">
                            {enhancedSyncStatus.statistics.breakdown.products.toLocaleString()}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Productos
                          </Typography>
                        </Box>
                      </Grid>
                      
                      <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                        <Box sx={{ textAlign: 'center', p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                          <Typography variant="h5" color="secondary.main">
                            {enhancedSyncStatus.statistics.breakdown.sales.toLocaleString()}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Ventas
                          </Typography>
                        </Box>
                      </Grid>
                      
                      <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                        <Box sx={{ textAlign: 'center', p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                          <Typography variant="h5" color="success.main">
                            {enhancedSyncStatus.statistics.breakdown.inventory.toLocaleString()}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Inventario
                          </Typography>
                        </Box>
                      </Grid>
                      
                      <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                        <Box sx={{ textAlign: 'center', p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                          <Typography variant="h5" color="info.main">
                            {enhancedSyncStatus.statistics.breakdown.users.toLocaleString()}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Usuarios
                          </Typography>
                        </Box>
                      </Grid>
                      
                      <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                        <Box sx={{ textAlign: 'center', p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                          <Typography variant="h5" color="warning.main">
                            {enhancedSyncStatus.statistics.breakdown.commissions.toLocaleString()}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Comisiones
                          </Typography>
                        </Box>
                      </Grid>
                      
                      <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                        <Box sx={{ textAlign: 'center', p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                          <Typography variant="h5" color="error.main">
                            {enhancedSyncStatus.statistics.breakdown.transfers.toLocaleString()}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Transferencias
                          </Typography>
                        </Box>
                      </Grid>
                      
                      <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                        <Box sx={{ textAlign: 'center', p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                          <Typography variant="h5" color="text.primary">
                            {enhancedSyncStatus.statistics.breakdown.cashDrawerSessions.toLocaleString()}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Sesiones de Caja
                          </Typography>
                        </Box>
                      </Grid>
                    </Grid>
                    
                    <Box sx={{ mt: 2, p: 2, backgroundColor: 'action.hover', borderRadius: 1 }}>
                      <Typography variant="subtitle2" gutterBottom>
                        Resumen Total
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Total de registros disponibles: <strong>{enhancedSyncStatus.statistics.totalRecords.toLocaleString()}</strong>
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Tienda: <strong>{enhancedSyncStatus.storeId}</strong>
                      </Typography>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            )}
          </Grid>
        </TabPanel>

        {/* Panel de Sistema */}
        <TabPanel value={currentTab} index={5}>
          <Typography variant="h6" gutterBottom fontWeight="bold">
            Configuraciones del Sistema
          </Typography>
          
          <Grid container spacing={3}>
            <Grid size={{ xs: 12, md: 6 }}>
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                    Localización
                  </Typography>
                  
                  <FormControl fullWidth sx={{ mb: 2 }}>
                    <InputLabel>Idioma</InputLabel>
                    <Select
                      value={settings.system.language}
                      onChange={(e) => updateSettings('system', 'language', e.target.value)}
                      label="Idioma"
                    >
                      <MenuItem value="es">🇭🇳 Español (Honduras)</MenuItem>
                      <MenuItem value="en">🇺🇸 English</MenuItem>
                    </Select>
                  </FormControl>
                  
                  <FormControl fullWidth sx={{ mb: 2 }}>
                    <InputLabel>Moneda</InputLabel>
                    <Select
                      value={settings.system.currency}
                      onChange={(e) => updateSettings('system', 'currency', e.target.value)}
                      label="Moneda"
                    >
                      <MenuItem value="HNL">L (Lempira Hondureña)</MenuItem>
                      <MenuItem value="USD">$ (Dólar Americano)</MenuItem>
                    </Select>
                  </FormControl>
                  
                  <FormControl fullWidth sx={{ mb: 2 }}>
                    <InputLabel>Formato de fecha</InputLabel>
                    <Select
                      value={settings.system.dateFormat}
                      onChange={(e) => updateSettings('system', 'dateFormat', e.target.value)}
                      label="Formato de fecha"
                    >
                      <MenuItem value="DD/MM/YYYY">DD/MM/YYYY</MenuItem>
                      <MenuItem value="MM/DD/YYYY">MM/DD/YYYY</MenuItem>
                      <MenuItem value="YYYY-MM-DD">YYYY-MM-DD</MenuItem>
                    </Select>
                  </FormControl>
                  
                  <FormControl fullWidth>
                    <InputLabel>Formato de hora</InputLabel>
                    <Select
                      value={settings.system.timeFormat}
                      onChange={(e) => updateSettings('system', 'timeFormat', e.target.value)}
                      label="Formato de hora"
                    >
                      <MenuItem value="24h">24 horas (14:30)</MenuItem>
                      <MenuItem value="12h">12 horas (2:30 PM)</MenuItem>
                    </Select>
                  </FormControl>
                </CardContent>
              </Card>
            </Grid>
            
            <Grid size={{ xs: 12, md: 6 }}>
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                    Seguridad y Respaldos
                  </Typography>
                  
                  <TextField
                    fullWidth
                    label="Tiempo de sesión (minutos)"
                    type="number"
                    value={settings.system.sessionTimeout}
                    onChange={(e) => updateSettings('system', 'sessionTimeout', parseInt(e.target.value) || 480)}
                    sx={{ mb: 2 }}
                    helperText="0 = sesión sin expiración"
                  />
                  
                  <FormControl fullWidth sx={{ mb: 2 }}>
                    <InputLabel>Frecuencia de respaldo</InputLabel>
                    <Select
                      value={settings.system.backupFrequency}
                      onChange={(e) => updateSettings('system', 'backupFrequency', e.target.value)}
                      label="Frecuencia de respaldo"
                    >
                      <MenuItem value="manual">Manual</MenuItem>
                      <MenuItem value="daily">Diario</MenuItem>
                      <MenuItem value="weekly">Semanal</MenuItem>
                      <MenuItem value="monthly">Mensual</MenuItem>
                    </Select>
                  </FormControl>
                  
                  <FormControl fullWidth>
                    <InputLabel>Tema</InputLabel>
                    <Select
                      value={settings.system.theme}
                      onChange={(e) => updateSettings('system', 'theme', e.target.value)}
                      label="Tema"
                    >
                      <MenuItem value="light">☀️ Claro</MenuItem>
                      <MenuItem value="dark">🌙 Oscuro</MenuItem>
                      <MenuItem value="auto">🔄 Automático</MenuItem>
                    </Select>
                  </FormControl>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </TabPanel>
      </Paper>

      {/* Dialog de Guardar */}
      <Dialog open={saveDialog} onClose={() => setSaveDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <SaveIcon />
            Guardar Configuraciones
          </Box>
        </DialogTitle>
        <DialogContent>
          <Alert severity="info" sx={{ mb: 2 }}>
            Los cambios se aplicarán inmediatamente en todo el sistema.
          </Alert>
          
          <Typography variant="body1">
            ¿Estás seguro que deseas guardar todas las configuraciones realizadas?
          </Typography>
          
          {settings.inventory.allowNegativeStock && (
            <Alert severity="warning" sx={{ mt: 2 }}>
              <Typography variant="body2">
                <strong>Nota:</strong> Tienes activado el inventario negativo. 
                Esto permite procesar ventas incluso sin stock disponible.
              </Typography>
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSaveDialog(false)}>
            Cancelar
          </Button>
          <Button variant="contained" onClick={saveSettings}>
            Guardar Cambios
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog de Restablecer */}
      <Dialog open={resetDialog} onClose={() => setResetDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <RestoreIcon />
            Restablecer Configuraciones
          </Box>
        </DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            Esta acción restablecerá todas las configuraciones a sus valores predeterminados.
          </Alert>
          
          <Typography variant="body1">
            ¿Estás seguro que deseas restablecer todas las configuraciones? 
            Perderás todos los cambios personalizados.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setResetDialog(false)}>
            Cancelar
          </Button>
          <Button variant="contained" color="warning" onClick={resetToDefaults}>
            Restablecer
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SettingsPage;